/**
 * SummaryKpisCard — five-tile KPI strip at the top of /reports.
 *
 * Mirrors Dedrone's summary header: Total Alerts / Alerts per Day / Avg
 * Duration / Days with Alerts / Max Alerts per Day. Tiles stay on a single
 * row on xl, stack 2×2+1 under ~md, and collapse to full width on phones.
 */
import type { SummaryKpis } from '../../services/reportsApi';
import {
  BellAlertIcon,
  ChartBarIcon,
  ClockIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remMin = m % 60;
  return remMin ? `${h}h ${remMin}m` : `${h}h`;
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

interface Props {
  kpis: SummaryKpis;
}

export default function SummaryKpisCard({ kpis }: Props) {
  const tiles = [
    {
      label: 'Total Alerts',
      value: formatNumber(kpis.totalAlerts),
      icon: BellAlertIcon,
      tint: 'text-accent bg-accent/10',
    },
    {
      label: 'Alerts per Day',
      value: kpis.alertsPerDay.toFixed(1),
      icon: ChartBarIcon,
      tint: 'text-sky-400 bg-sky-400/10',
    },
    {
      label: 'Avg Alert Duration',
      value: formatDuration(kpis.avgDurationSec),
      icon: ClockIcon,
      tint: 'text-amber-400 bg-amber-400/10',
    },
    {
      label: 'Days with Alerts',
      value: formatNumber(kpis.daysWithAlerts),
      icon: CalendarDaysIcon,
      tint: 'text-emerald-400 bg-emerald-400/10',
    },
    {
      label: 'Max Alerts / Day',
      value: formatNumber(kpis.maxAlertsPerDay),
      icon: ArrowTrendingUpIcon,
      tint: 'text-rose-400 bg-rose-400/10',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <div
            key={t.label}
            className="glass-card p-4 flex items-center gap-4"
          >
            <div className={`h-10 w-10 flex-shrink-0 rounded-lg flex items-center justify-center ${t.tint}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-xs text-gray-500 truncate">{t.label}</div>
              <div className="text-2xl font-display font-bold text-white truncate">{t.value}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
